'use client'

import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { env } from 'next-runtime-env'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import AttachmentPreviewBySlug from '@/app/investor-relations/[companySlug]/AttachmentPreviewBySlug'
import type {
  IRDocument,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'
import type { Attachment } from '@/utils/types'
import InvestorRelationsDocumentViewer from '@/components/InvestorRelations/InvestorRelationsDocumentViewer'

interface CorporatePresentationSectionProps {
  presentations: IRDocument[]
  isPreview?: boolean
  organisationId?: string
  deepLinkedDocumentId?: string | null
  onDeepLinkedDocumentClose?: (sectionId: string) => void
  onDocumentOpen?: (sectionId: string, documentId: string) => void
  useContextFunction: () => InvestorRelationsConfigContextType
}

import type { IRCompanyConfig, IRThemeConfig } from '@/utils/investorRelations'

export const CorporatePresentationSection = forwardRef<
  HTMLElement,
  CorporatePresentationSectionProps
>(function CorporatePresentationSection(
  {
    presentations,
    isPreview = false,
    organisationId = undefined,
    deepLinkedDocumentId = null,
    onDeepLinkedDocumentClose,
    onDocumentOpen,
    useContextFunction,
  },
  ref
) {
  // Access config values using the hook
  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType

  // Extract published config values
  const companyConfig = ((config?.companyConfig as IRCompanyConfig) ||
    {}) as IRCompanyConfig
  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig

  const primaryColor = themeConfig.primaryColor
  const textColor = themeConfig.textColor
  const fontFamily = themeConfig.fontStyle

  const [documentOrientation, setDocumentOrientation] = useState<
    Record<string, boolean>
  >({})
  const [currentPresentationIndex, setCurrentPresentationIndex] = useState(0)
  const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] =
    useState<Attachment | null>(null)
  const attachmentPreviewer = useDisclosure()

  const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')

  const corporatePresentations = presentations || []
  const currentPresentation =
    corporatePresentations[currentPresentationIndex] || null

  const isPortrait = currentPresentation
    ? documentOrientation[currentPresentation.attachmentId] || false
    : false
  const aspectRatio = isPortrait ? '5 / 7' : '16 / 9'

  const handlePrevPresentation = () => {
    setCurrentPresentationIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNextPresentation = () => {
    setCurrentPresentationIndex((prev) =>
      Math.min(corporatePresentations.length - 1, prev + 1)
    )
  }

  useEffect(() => {
    if (!deepLinkedDocumentId || attachmentPreviewer.open) return

    const presentationIndex = corporatePresentations.findIndex(
      (presentation) => presentation.attachmentId === deepLinkedDocumentId
    )
    if (presentationIndex === -1) return

    const presentation = corporatePresentations[presentationIndex]
    setCurrentPresentationIndex(presentationIndex)
    setSelectedAttachmentForPreview(presentation as unknown as Attachment)
    attachmentPreviewer.onOpen()
  }, [
    attachmentPreviewer,
    attachmentPreviewer.open,
    corporatePresentations,
    deepLinkedDocumentId,
  ])

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
    const month = date.toLocaleString('default', { month: 'long' })
    const year = date.getFullYear()
    return `${month} ${year}`
  }

  return (
    <Box
      ref={ref}
      as="section"
      id="corporate-presentation"
      bg="white"
      minH="calc(100vh - 50px)"
      display="flex"
      alignItems="center"
      py={{ base: 10, md: 16 }}
    >
      <Container maxW="6xl">
        <Stack gap={{ base: 6, md: 10 }}>
          <Heading
            as="h2"
            size={{ base: 'lg', md: '5xl' }}
            color={primaryColor}
            fontWeight="bold"
            mb={3}
            textAlign="center"
            fontFamily={fontFamily}
          >
            Corporate Presentation
          </Heading>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 8, md: 12 }}
            align={{ base: 'stretch', md: 'flex-start' }}
            alignItems={{ base: 'stretch', md: 'flex-start' }}
          >
            {/* Left: featured presentation card */}
            <Box flex="1" minW={0} maxW="100%">
              {currentPresentation ? (
                <VStack gap={3} w="100%" maxH="fit-content">
                  {/* Presentation cover Container with consistent height for alignment */}
                  <Flex
                    w="100%"
                    alignItems="center"
                    justifyContent="center"
                    minW={0}
                  >
                    <Box
                      w={isPortrait ? '70%' : '100%'}
                      maxW="100%"
                      mx={isPortrait ? 'auto' : '0'}
                      bg="white"
                      borderRadius={isPortrait ? '0' : 'lg'}
                      border="1px solid"
                      borderColor="gray.200"
                      overflow="hidden"
                      shadow="sm"
                      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                      transition="all 0.2s"
                      cursor="pointer"
                      position="relative"
                      style={{
                        aspectRatio,
                        transformOrigin: 'center center',
                        maxWidth: '100%',
                      }}
                      maxH="80vh"
                      onClick={() => {
                        setSelectedAttachmentForPreview(
                          currentPresentation as unknown as Attachment
                        )
                        attachmentPreviewer.onOpen()
                        onDocumentOpen?.(
                          'corporate-presentation',
                          currentPresentation.attachmentId
                        )
                      }}
                    >
                      <AttachmentPreviewBySlug
                        attachmentType={currentPresentation.attachmentType}
                        fileName={currentPresentation.fileName}
                        fileType={
                          'fileType' in currentPresentation
                            ? currentPresentation.fileType
                            : undefined
                        }
                        thumbnailUrl={currentPresentation.thumbnailUrl}
                        attachmentId={currentPresentation.attachmentId}
                        companySlug={config?.companySlugPublished || ''}
                        section="corporate"
                        apiBaseUrl={NEXT_PUBLIC_ASSIDUOUS_API_URL}
                        onOrientationDetected={(isPortrait: boolean) => {
                          setDocumentOrientation((prev) => {
                            if (
                              prev[currentPresentation.attachmentId] ===
                              isPortrait
                            ) {
                              return prev
                            }

                            return {
                              ...prev,
                              [currentPresentation.attachmentId]: isPortrait,
                            }
                          })
                        }}
                        subheading={
                          'subheading' in currentPresentation
                            ? currentPresentation.subheading
                            : undefined
                        }
                        duration={
                          'duration' in currentPresentation &&
                          typeof currentPresentation.duration === 'number'
                            ? currentPresentation.duration
                            : undefined
                        }
                        isPreview={isPreview}
                        organisationId={organisationId || ''}
                      />
                    </Box>
                  </Flex>

                  {/* Date and Title */}
                  <Flex
                    gap={1}
                    alignItems="flex-start"
                    flexDirection="column"
                    w="100%"
                  >
                    <Text
                      fontSize="sm"
                      textAlign="left"
                      color="gray.600"
                      fontFamily={fontFamily}
                    >
                      {currentPresentation.publishedDate === '[Not published]'
                        ? 'Not published'
                        : formatDate(currentPresentation.publishedDate)}
                    </Text>
                    <Text
                      fontSize="lg"
                      color={textColor}
                      fontWeight="bold"
                      fontFamily={fontFamily}
                    >
                      {currentPresentation.fileName}
                    </Text>
                  </Flex>

                  {/* Navigation Controls - for switching between presentation files */}
                  {corporatePresentations.length > 1 && (
                    <HStack
                      justify="center"
                      gap={2}
                      w="100%"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        aria-label="Previous presentation"
                        onClick={handlePrevPresentation}
                        disabled={currentPresentationIndex === 0}
                        variant="ghost"
                        size="sm"
                        color={primaryColor}
                      >
                        <Icon as={FaChevronLeft} />
                      </Button>

                      {/* Pagination Dots - one per presentation file */}
                      <HStack gap={1}>
                        {corporatePresentations.map((presentation, index) => {
                          const isActive = index === currentPresentationIndex
                          return (
                            <Box
                              key={presentation.attachmentId}
                              w={isActive ? '8px' : '6px'}
                              h={isActive ? '8px' : '6px'}
                              borderRadius="full"
                              bg={isActive ? primaryColor : 'gray.400'}
                              cursor="pointer"
                              onClick={() => setCurrentPresentationIndex(index)}
                              transition="all 0.2s"
                              _hover={{
                                bg: isActive ? primaryColor : 'gray.500',
                              }}
                            />
                          )
                        })}
                      </HStack>

                      <Button
                        aria-label="Next presentation"
                        onClick={handleNextPresentation}
                        disabled={
                          currentPresentationIndex ===
                          corporatePresentations.length - 1
                        }
                        variant="ghost"
                        size="sm"
                        color={primaryColor}
                      >
                        <Icon as={FaChevronRight} />
                      </Button>
                    </HStack>
                  )}
                </VStack>
              ) : (
                <Box
                  borderRadius="lg"
                  overflow="hidden"
                  bg={primaryColor}
                  color="white"
                  position="relative"
                  p={{ base: 6, md: 8 }}
                  minH={{ base: '220px', md: '260px' }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  boxShadow="lg"
                >
                  <Stack gap={4}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      opacity={0.8}
                      fontFamily={fontFamily}
                    >
                      {companyConfig?.companyName}
                    </Text>
                    <Heading as="h3" size="md" fontFamily={fontFamily}>
                      Corporate Presentation
                    </Heading>
                    <Text
                      fontSize="sm"
                      maxW="xs"
                      opacity={0.9}
                      fontFamily={fontFamily}
                    >
                      We are preparing our presentations. Check back soon for
                      our latest updates and company announcements.
                    </Text>
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Right: About section */}
            <Box flex="1" maxW={{ md: '420px' }}>
              <Stack gap={4}>
                <Heading
                  as="h2"
                  size="lg"
                  color={primaryColor}
                  fontFamily={fontFamily}
                >
                  About us
                </Heading>
                <Text
                  fontSize="md"
                  color={textColor}
                  lineHeight="tall"
                  fontFamily={fontFamily}
                >
                  {companyConfig?.companyDescription}
                </Text>
              </Stack>
            </Box>
          </Flex>
        </Stack>
      </Container>

      <InvestorRelationsDocumentViewer
        attachment={selectedAttachmentForPreview}
        isOpen={attachmentPreviewer.open}
        anonymousMode={true}
        companySlug={config?.companySlugPublished || ''}
        section="corporate"
        isPreview={isPreview}
        organisationId={organisationId}
        companyLogoUrl={companyConfig.logoUrl}
        onClose={() => {
          const closedAttachmentId = selectedAttachmentForPreview?.attachmentId
          attachmentPreviewer.onClose()
          setSelectedAttachmentForPreview(null)
          if (closedAttachmentId === deepLinkedDocumentId) {
            onDeepLinkedDocumentClose?.('corporate-presentation')
          }
        }}
      />
    </Box>
  )
})
